import React, { useState, useEffect } from "react";
import { FaTimes, FaCrown, FaUser, FaCheck, FaEdit } from "react-icons/fa";
import { getRequest, postRequest } from "../../../helpers/api.helper";
import { toast } from "react-toastify";
import { PackageDetailsType, PackageType, ServiceType } from "../../../types";
import { UPLOAD_URL } from "../../../utils/constants";
import {
  getPrivacyOverlayLabel,
  shouldBlurProfileImage,
} from "../../../utils/imagePrivacy";

// ✅ Define props type for your component
interface ServicePreferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any; // replace with your real ProfileType if available
  onSuccess?: () => void;
}

type ServiceObjType = {
  value: ServiceType;
  label: string;
  icon: string;
  description: string;
};

const ServicePreferenceModal: React.FC<ServicePreferenceModalProps> = ({
  isOpen,
  onClose,
  profile,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [packageDetails, setPackageDetails] =
    useState<PackageDetailsType | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(
    null,
  );
  const [packageInfo, setPackageInfo] = useState<PackageType | null>(null);
  // Service options
  const serviceOptions: ServiceObjType[] = [
    {
      value: "only_online",
      label: "Only Online Service",
      icon: "🌐",
      description: "Online profiles only",
    },
    {
      value: "only_offline",
      label: "Only Offline Service",
      icon: "🏢",
      description: "Offline bureau service",
    },
    {
      value: "online_offline",
      label: "Online & Offline Service",
      icon: "🔄",
      description: "Both online and offline",
    },
  ];

  // Validity days options
  const validityDaysOptions = [
    { value: 30, label: "30 Days" },
    { value: 60, label: "60 Days" },
    { value: 90, label: "90 Days" },
    { value: 180, label: "180 Days" },
    { value: 365, label: "1 Year" },
  ];

  // Visible contact numbers options
  const contactNumbersOptions = [
    { value: 5, label: "5 Profiles" },
    { value: 10, label: "10 Profiles" },
    { value: 20, label: "20 Profiles" },
    { value: 50, label: "50 Profiles" },
    { value: 100, label: "100 Profiles" },
    { value: -1, label: "Unlimited" },
  ];

  const fetchPackageDetails = async () => {
    try {
      setLoading(true);
      const response = await getRequest(
        `/bureau/packages/profile/${profile._id}`,
      );
      if (response.success) {
        if (response.data) {
          setPackageDetails(response.data);
          setSelectedService(response.data.package.service);
          setPackageInfo(response.data.package);
        }
      }
    } catch (error) {
      console.error("Error fetching package details:", error);
      // If error, assume free user
      setSelectedService(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch package details
  useEffect(() => {
    if (isOpen && profile?._id) {
      fetchPackageDetails();
    }
  }, [isOpen, profile]);

  const handleServiceChange = (service: ServiceType) => {
    setSelectedService(service);
  };

  const handleInputChange = (field: keyof PackageType, value: any) => {
    setPackageInfo((prev) => ({
      ...(prev ?? { service: selectedService || "free" }), // handle null
      [field]: value,
    }));
  };

  const saveActivatePackage = async () => {
    try {
      if (!selectedService) {
        toast.warning("Please select a service preference");
        return;
      }

      // Validation based on service type
      // Validation based on service type
      if (selectedService === "only_online") {
        if (!packageInfo?.payingAmount)
          return toast.warning("Please enter paying amount (₹).");
        if (!packageInfo?.visibleContactNumbers)
          return toast.warning(
            "Please select number of visible contact profiles.",
          );
        if (!packageInfo?.validityDays)
          return toast.warning("Please select validity days for the package.");
      } else if (selectedService === "only_offline") {
        if (!packageInfo?.registrationFees)
          return toast.warning("Please enter registration fees (₹).");
        if (!packageInfo?.goodwillAmount)
          return toast.warning("Please enter goodwill amount (₹).");
        if (!packageInfo?.validityDays)
          return toast.warning(
            "Please select validity days for the offline package.",
          );
      } else if (selectedService === "online_offline") {
        if (!packageInfo?.payingAmount)
          return toast.warning("Please enter online paying amount (₹).");
        if (!packageInfo?.registrationFees)
          return toast.warning("Please enter offline registration fees (₹).");
        if (!packageInfo?.goodwillAmount)
          return toast.warning("Please enter offline goodwill amount (₹).");
        if (!packageInfo?.validityDays)
          return toast.warning("Please select combined validity days.");
      }

      const payload = {
        userId: profile._id,
        action: "activate", // handled in backend unified route
        package: {
          service: selectedService,
          payingAmount: Number(packageInfo?.payingAmount) || 0,
          visibleContactNumbers:
            Number(packageInfo?.visibleContactNumbers) || 0,
          validityDays: Number(packageInfo?.validityDays) || 0,
          registrationFees: Number(packageInfo?.registrationFees) || 0,
          goodwillAmount: Number(packageInfo?.goodwillAmount) || 0,
        },
      };

      setLoading(true);
      const response = await postRequest(
        `/bureau/package/${profile._id}`,
        payload,
      );

      if (response.success) {
        toast.success("Package created and activated successfully!");
        await fetchPackageDetails(); // Refresh data
        onSuccess && onSuccess();
        onClose();
      } else {
        toast.error(response.message || "Failed to activate package");
      }
    } catch (error: any) {
      console.error("Error activating package:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong while activating package.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (
      !window.confirm(
        "Are you sure you want to move this profile to free membership?",
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      const response = await postRequest(`/bureau/package/${profile._id}`, {
        action: "deactivate",
        // paymentStatus: "completed",
      });
      if (response.success) {
        toast.success("Profile moved to free membership successfully!");
        // Refresh package details
        await fetchPackageDetails();
        onSuccess && onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error deactivating package:", error);
      toast.error("Error deactivating package. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold flex items-center">
            <FaCrown className="mr-2 text-yellow-500" />
            Service & Package Management
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            <FaTimes />
          </button>
        </div>

        {loading && (
          <div className="text-center py-4">
            <div className="inline-flex items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
              Loading...
            </div>
          </div>
        )}

        {/* Profile Info */}
        <div className="bg-gray-50 p-4 rounded-lg mb-2">
          <div className="flex items-center">
            <div className="relative w-16 h-16 mr-4">
              <img
                src={
                  profile?.image
                    ? `${UPLOAD_URL}/users/profile_pic/${profile.image}`
                    : "https://www.pngitem.com/pimgs/m/581-5813504_avatar-dummy-png-transparent-png.png"
                }
                alt="Profile"
                className={`w-full h-full object-cover rounded-full ${
                  shouldBlurProfileImage(profile?.imagePrivacy)
                    ? "blur-lg scale-110 brightness-90"
                    : ""
                }`}
              />
              {shouldBlurProfileImage(profile?.imagePrivacy) && (
                <div className="absolute inset-0 bg-black/50 rounded-full text-white text-[8px] font-semibold tracking-wide uppercase flex flex-col items-center justify-center text-center pointer-events-none px-2">
                  <span>{getPrivacyOverlayLabel(profile?.imagePrivacy)}</span>
                  <span className="text-[7px] opacity-80">Hidden</span>
                </div>
              )}
            </div>
            <div>
              <h3 className="text-md font-semibold">{profile?.fullName}</h3>
              <p className="text-gray-600 text-sm">ID: {profile?.martialId}</p>
              <p className="text-gray-600 text-sm">
                Current Status:{" "}
                {packageDetails?.package.service === "free"
                  ? "Free Member"
                  : "Paid Member"}
              </p>
            </div>
          </div>
        </div>

        {/* Current Package Status */}
        {packageDetails && (
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold mb-2">Current Package Status</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium">Service:</span>{" "}
                {packageDetails.package.service}
              </div>
              <div>
                <span className="font-medium">Status:</span>
                <span
                  className={`ml-1 ${
                    packageDetails.isActive ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {packageDetails.isActive ? "Active" : "Inactive"}
                </span>
              </div>
              <div>
                <span className="font-medium">Payment:</span>
                <span
                  className={`ml-1 ${
                    packageDetails.paymentStatus === "completed"
                      ? "text-green-600"
                      : packageDetails.paymentStatus === "expired"
                        ? "text-red-600"
                        : "text-yellow-600"
                  }`}
                >
                  {packageDetails.paymentStatus}
                </span>
              </div>
              {/* <div>
                <span className="font-medium">Views:</span>{" "}
                {packageDetails.viewsCount || 0}
              </div> */}
              {packageDetails.expiresAt && (
                <div className="md:col-span-2">
                  <span className="font-medium">Expires:</span>{" "}
                  {new Date(packageDetails.expiresAt).toLocaleDateString()}
                  {new Date(packageDetails.expiresAt) < new Date() && (
                    <span className="ml-2 text-red-600 font-medium">
                      (EXPIRED)
                    </span>
                  )}
                </div>
              )}
              {packageDetails.activatedAt && (
                <div className="md:col-span-2">
                  <span className="font-medium">Activated:</span>{" "}
                  {new Date(packageDetails.activatedAt).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Service Selection */}
        <div className="mb-6">
          <h3 className="text-md font-semibold mb-2">
            Select Service Preference
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {serviceOptions.map((service) => (
              <div
                key={service.value}
                onClick={() => handleServiceChange(service.value)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedService === service.value
                    ? "border-gray-500 bg-gray-50"
                    : "border-gray-200 hover:border-blue-300"
                }`}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{service.icon}</span>
                  <div>
                    <p className="font-medium text-sm">{service.label}</p>
                    <p className="text-sm text-gray-600">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Package Details Form - Only show for paid services */}
        {selectedService && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Package Details</h3>
            </div>

            <div className="space-y-6">
              {/* Only Online Service */}
              {selectedService === "only_online" && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">
                    Only Online Service Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Paying Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={packageInfo?.payingAmount}
                        onChange={(e) =>
                          handleInputChange("payingAmount", e.target.value)
                        }
                        className="w-full p-2 border rounded"
                        placeholder="Enter amount"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Visible Contact Numbers
                      </label>
                      <select
                        value={packageInfo?.visibleContactNumbers}
                        onChange={(e) =>
                          handleInputChange(
                            "visibleContactNumbers",
                            e.target.value,
                          )
                        }
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select</option>
                        {contactNumbersOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Validity Days
                      </label>
                      <select
                        value={packageInfo?.validityDays}
                        onChange={(e) =>
                          handleInputChange("validityDays", e.target.value)
                        }
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select</option>
                        {validityDaysOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Only Offline Service */}
              {selectedService === "only_offline" && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">
                    Only Offline Service Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Registration Fees (₹)
                      </label>
                      <input
                        type="number"
                        value={packageInfo?.registrationFees}
                        onChange={(e) =>
                          handleInputChange("registrationFees", e.target.value)
                        }
                        className="w-full p-2 border rounded"
                        placeholder="Enter fees"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Goodwill Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={packageInfo?.goodwillAmount}
                        onChange={(e) =>
                          handleInputChange("goodwillAmount", e.target.value)
                        }
                        className="w-full p-2 border rounded"
                        placeholder="Enter amount"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Validity Days
                      </label>
                      <select
                        value={packageInfo?.validityDays}
                        onChange={(e) =>
                          handleInputChange("validityDays", e.target.value)
                        }
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select</option>
                        {validityDaysOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Online & Offline Service */}
              {selectedService === "online_offline" && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">
                    Online & Offline Service Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Online Paying Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={packageInfo?.payingAmount}
                        onChange={(e) =>
                          handleInputChange("payingAmount", e.target.value)
                        }
                        className="w-full p-2 border rounded"
                        placeholder="Enter amount"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Offline Registration Fees (₹)
                      </label>
                      <input
                        type="number"
                        value={packageInfo?.registrationFees}
                        onChange={(e) =>
                          handleInputChange("registrationFees", e.target.value)
                        }
                        className="w-full p-2 border rounded"
                        placeholder="Enter fees"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Offline Goodwill Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={packageInfo?.goodwillAmount}
                        onChange={(e) =>
                          handleInputChange("goodwillAmount", e.target.value)
                        }
                        className="w-full p-2 border rounded"
                        placeholder="Enter amount"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Combined Validity Days
                      </label>
                      <select
                        value={packageInfo?.validityDays}
                        onChange={(e) =>
                          handleInputChange("validityDays", e.target.value)
                        }
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select</option>
                        {validityDaysOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Combined Visible Contact Numbers
                      </label>
                      <select
                        value={packageInfo?.visibleContactNumbers || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "visibleContactNumbers",
                            e.target.value,
                          )
                        }
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select</option>
                        {contactNumbersOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Action Buttons */}
        <div className="flex items-center justify-between gap-4">
          {/* Move to Free (only if currently paid & active) */}
          <button
            onClick={handleDeactivate}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center"
          >
            <FaUser className="mr-2" />
            Move to Free
          </button>

          <button
            onClick={saveActivatePackage}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save & Activate"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServicePreferenceModal;
